import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

export type HorarioItem = {
  hora: string;
  plazas_disponibles: number;
  recursos_disponibles: number;
  recurso_numero: number;
};

/* ============================================================
   useObtenerHorariosPorRango
   Única fuente de verdad. Una sola llamada RPC para todo el
   rango. Los días y horas se derivan del resultado en memoria.
============================================================ */

export const useObtenerHorariosPorRango = (
  id_usuario: string | null | undefined,
  fecha_inicio: string,
  fecha_fin: string,
  servicio_uuid: string | undefined,
  duracion_minutos?: number | null
) => {
  const [horariosPorDia, setHorariosPorDia] = useState<
    Record<string, HorarioItem[]>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHorariosRango = useCallback(async () => {
    if (!fecha_inicio || !fecha_fin || !servicio_uuid) {
      setHorariosPorDia({});
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      'obtener_horarios_trabajador_rango',
      {
        p_fecha_inicio: fecha_inicio.split('T')[0],
        p_fecha_fin: fecha_fin.split('T')[0],
        p_usuario_uuid: id_usuario ?? null,
        p_servicio_uuid: servicio_uuid,
        p_duracion_minutos: duracion_minutos ?? null,
      }
    );

    if (rpcError) {
      Sentry.captureException(rpcError, {
        extra: { id_usuario, fecha_inicio, fecha_fin, servicio_uuid },
      });
      setError('Error al obtener horarios disponibles');
      toast.error('Error al obtener horarios disponibles');
      setHorariosPorDia({});
    } else {
      const agrupado: Record<string, HorarioItem[]> = {};
      (data || []).forEach((h: any) => {
        if (h.estado !== 'disponible') return;
        if (!agrupado[h.fecha]) agrupado[h.fecha] = [];
        agrupado[h.fecha].push({
          hora: h.hora_inicio as string,
          plazas_disponibles: h.plazas_disponibles ?? 0,
          recursos_disponibles: h.recursos_disponibles ?? 0,
          recurso_numero: h.recurso_numero ?? 1,
        });
      });
      setHorariosPorDia(agrupado);
    }

    setLoading(false);
  }, [id_usuario, fecha_inicio, fecha_fin, servicio_uuid, duracion_minutos]);

  useEffect(() => {
    fetchHorariosRango();
  }, [fetchHorariosRango]);

  return {
    horariosPorDia,
    loading,
    error,
    refetchHorarios: fetchHorariosRango,
  };
};
